import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { SrIcon } from "@/components/ui/SrIcon";
import ContractStatusBadge from "./ContractStatusBadge";
import {
  useGenerateContract,
  useSendContractToSigning,
  useContractPdfUrl,
  type ContractStatus,
  type ContractTemplateType,
  type ExtraContract,
} from "@/hooks/use-extra-contracts";

const TEMPLATE_LABELS: Record<ContractTemplateType, string> = {
  statist: "Statistavtale",
  ekstern_skuespiller: "Avtale — ekstern skuespiller",
  location: "Leieavtale — location",
};

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  extraId: string;
  extraName: string;
  projectId: string;
  /** Eksisterende kontrakt for denne personen, hvis noen (fra StatisterTab). */
  contract: ExtraContract | undefined;
}

/**
 * VIB-1281/1282: velg maltype → generer kontrakt-PDF → forhåndsvis via
 * signert URL → send til signering. Bak feature_contracts i callers.
 */
export default function GenerateContractDialog({
  open,
  onOpenChange,
  extraId,
  extraName,
  projectId,
  contract,
}: Props) {
  const [templateType, setTemplateType] = useState<ContractTemplateType>("statist");
  const [contractId, setContractId] = useState<string | null>(contract?.id ?? null);
  const [status, setStatus] = useState<ContractStatus | undefined>(contract?.status);
  const [generatedPath, setGeneratedPath] = useState<string | null>(contract?.pdf_path ?? null);
  const generateMut = useGenerateContract();
  const sendMut = useSendContractToSigning();
  const { data: previewUrl, isFetching: previewLoading } = useContractPdfUrl(generatedPath);

  const handleGenerate = () => {
    generateMut.mutate(
      { extra_id: extraId, template_type: templateType, project_id: projectId },
      {
        onSuccess: (data) => {
          setGeneratedPath(data.pdf_path);
          setContractId(data.contract_id);
          setStatus(data.status);
        },
      },
    );
  };

  const handleSend = () => {
    if (!contractId) return;
    sendMut.mutate(
      { extra_id: extraId, contract_id: contractId, project_id: projectId },
      { onSuccess: () => setStatus("sendt") },
    );
  };

  const handleClose = (next: boolean) => {
    if (!next) {
      setGeneratedPath(contract?.pdf_path ?? null);
      setContractId(contract?.id ?? null);
      setStatus(contract?.status);
    }
    onOpenChange(next);
  };

  const canSend = status === "utkast" && !!contractId;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Kontrakt — {extraName}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {status && (
            <div>
              <ContractStatusBadge status={status} />
            </div>
          )}
          <Select value={templateType} onValueChange={(v) => setTemplateType(v as ContractTemplateType)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(Object.keys(TEMPLATE_LABELS) as ContractTemplateType[]).map((t) => (
                <SelectItem key={t} value={t}>
                  {TEMPLATE_LABELS[t]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {generatedPath && (
            <div className="text-xs">
              {previewLoading ? (
                <span className="text-muted-foreground">Forbereder forhåndsvisning...</span>
              ) : previewUrl ? (
                <a
                  href={previewUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline inline-flex items-center gap-1"
                >
                  <SrIcon name="file-text" className="w-3 h-3" /> Åpne kontrakt-PDF
                </a>
              ) : (
                <span className="text-destructive">Kunne ikke lage forhåndsvisning</span>
              )}
            </div>
          )}
        </div>
        <DialogFooter className="flex-wrap gap-2">
          <Button variant="outline" onClick={() => handleClose(false)}>
            Lukk
          </Button>
          {canSend && (
            <Button variant="secondary" onClick={handleSend} disabled={sendMut.isPending}>
              {sendMut.isPending ? (
                <SrIcon name="loader2" className="w-3 h-3 mr-1 animate-spin" />
              ) : (
                <SrIcon name="send" className="w-3 h-3 mr-1" />
              )}
              Send til signering
            </Button>
          )}
          <Button onClick={handleGenerate} disabled={generateMut.isPending || status === "sendt" || status === "signert"}>
            {generateMut.isPending ? (
              <SrIcon name="loader2" className="w-3 h-3 mr-1 animate-spin" />
            ) : (
              <SrIcon name="file-text" className="w-3 h-3 mr-1" />
            )}
            {generatedPath ? "Regenerer" : "Generer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
